import { formatClientDate, safeParseClientDate } from 'shared/util/datetime-fns';
import type { LmsCheckboxTree, LmsDateRange, LmsFilters, LmsNumberRange, LmsType } from '@/interfaces/lms';

// Recursive function that follows a path in a nested CheckboxTree, and then toggles the last element
export const toggleNestedCheckbox = (tree: LmsCheckboxTree, path: string[]): LmsCheckboxTree => {
    const [head, ...rest] = path;
    if (rest.length <= 0) {
        const { [head]: toggledOn, ...final } = tree;
        return toggledOn ? final : { ...final, [head]: {} };
    }
    return { ...tree, [head]: toggleNestedCheckbox(tree[head] ?? {}, rest) };
};

const parseAncestors = (destination: string): string[] => {
    const all = destination.split(';');
    if (all.length === 1) return all;
    const result = [];
    while (all.length > 1) {
        result.push(all.join(';'));
        all.pop();
    }
    return result.reverse();
};

const parseDestinations = (destinations: string[] | undefined): LmsCheckboxTree | undefined =>
    destinations?.reduce(
        (hierarchy: LmsCheckboxTree, dest: string): LmsCheckboxTree => toggleNestedCheckbox(hierarchy, parseAncestors(dest)),
        {},
    );

const parseNumberRange = (param: string | null): LmsNumberRange | undefined => {
    const parts = param?.split('-');
    if (parts?.length !== 2) {
        return undefined;
    }
    return {
        from: Number(parts[0]),
        to: Number(parts[1]),
    };
};

const parseDateRange = (param: string | null): LmsDateRange | undefined => {
    const parts = param?.split('-');
    if (parts?.length !== 2) {
        return undefined;
    }
    return {
        from: safeParseClientDate(parts[0]),
        to: safeParseClientDate(parts[1]),
    };
};

const parseTravelType = (types: string | null): LmsType[] | undefined =>
    types?.split(',').filter((type: string): type is LmsType => ['enveisfly', 'fly', 'spesifiert', 'uspesifisert'].includes(type));

const parseSortKey = (key: string | null): LmsFilters['sorter'] | undefined => (key === 'avreise' || key === 'pris_lms' ? key : undefined);

const parseNumber = (number: string | null): number | undefined => (number ? Number(number) : undefined);

export const convertQueryParamsToFilters = (params: URLSearchParams): LmsFilters => ({
    fra: params.get('fra')?.split(','),
    til: parseDestinations(params.get('til')?.split(',')),
    avreise: parseDateRange(params.get('avreise')),
    lengde: parseNumberRange(params.get('lengde')),
    type: parseTravelType(params.get('type')),
    pris: parseNumberRange(params.get('pris')),
    stjerner: params.get('stjerner')?.split(','),
    med: params.get('med')?.split(','),
    sorter: parseSortKey(params.get('sorter')),
    pageNumber: parseNumber(params.get('pageNumber')),
});

// Finds every leaf node in the tree and returns them as a list
const checkboxTreeToQueryParams = (map: LmsCheckboxTree): string[] | undefined => {
    const values = Object.keys(map)
        .map((key) => (Object.keys(map[key]).length === 0 ? [key] : checkboxTreeToQueryParams(map[key])) || [])
        .reduce((acc, val) => acc.concat(val), []);
    return values.length === 0 ? undefined : values;
};

export const convertFiltersToQueryParams = (filters: LmsFilters): Record<string, string | string[]> => {
    const queryParams: Record<string, string | string[] | undefined> = {
        fra: filters.fra,
        til: filters.til ? checkboxTreeToQueryParams(filters.til) : undefined,
        avreise: filters.avreise ? [formatClientDate(filters.avreise.from), formatClientDate(filters.avreise.to)].join('-') : undefined,
        lengde: filters.lengde ? [filters.lengde.from, filters.lengde.to].join('-') : undefined,
        type: filters.type,
        pris: filters.pris ? [filters.pris.from, filters.pris.to].join('-') : undefined,
        stjerner: filters.stjerner,
        med: filters.med,
        sorter: filters.sorter,
        pageNumber: filters.pageNumber?.toString(),
    };
    // Remove values that are undefined or empty arrays
    Object.keys(queryParams).forEach((key) => {
        const value = queryParams[key];
        if (value === undefined || (Array.isArray(value) && !value.length)) {
            delete queryParams[key];
        }
    });
    return queryParams as Record<string, string | string[]>;
};

export const updateListFilter = (value: string, list?: string[]): string[] => {
    if (!list) return [value];
    return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
};
